import React, { useEffect, useState } from 'react';
import { 
  FaTimes, 
  FaSave, 
  FaUpload, 
  FaTrash, 
  FaUser,
  FaIdCard,
  FaUserTag,
  FaBirthdayCake,
  FaVenusMars,
  FaCalendarAlt,
  FaPhone,
  FaMapMarkerAlt
} from '../../../utils/iconUtils';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: any) => void;
  initialData?: Partial<MemberForm>;
}

interface MemberForm {
  profilePhoto: File | null;
  photoUrl?: string;
  memberId: string;
  fullName: string;
  dateOfBirth: string;
  gender: string;
  registrationDate: string;
  contact: string;
  address: string;
  removePhoto?: boolean;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hasExistingPhoto, setHasExistingPhoto] = useState(false);

  const [formData, setFormData] = useState<MemberForm>({
    profilePhoto: null,
    photoUrl: '',
    memberId: '',
    fullName: '',
    dateOfBirth: '',
    gender: 'Male',
    registrationDate: '',
    contact: '',
    address: '',
    removePhoto: false
  });

  useEffect(() => {
    if (!isOpen) return;
    
    if (initialData) {
      const existingPhoto = !!initialData.photoUrl;
      setHasExistingPhoto(existingPhoto);
      
      setFormData(prev => ({
        ...prev,
        ...initialData,
        photoUrl: initialData.photoUrl ?? prev.photoUrl,
        removePhoto: false
      }));
      
      setPreviewUrl(initialData.photoUrl ?? null);
    } else {
      // Reset when opening for create
      setFormData({
        profilePhoto: null,
        photoUrl: '',
        memberId: '',
        fullName: '',
        dateOfBirth: '',
        gender: 'Male',
        registrationDate: '',
        contact: '',
        address: '',
        removePhoto: false
      });
      setPreviewUrl(null);
      setHasExistingPhoto(false);
    }
    
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    
    // Revoke previous object URL
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    
    if (file) {
      const objUrl = URL.createObjectURL(file);
      setPreviewUrl(objUrl);
      setFormData(prev => ({ 
        ...prev, 
        profilePhoto: file, 
        photoUrl: undefined,
        removePhoto: false
      }));
    } else {
      setPreviewUrl(formData.photoUrl ?? null);
      setFormData(prev => ({ ...prev, profilePhoto: null }));
    }
  };

  const handleRemovePhoto = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setPreviewUrl(null);
    setFormData(prev => ({ 
      ...prev, 
      profilePhoto: null,
      photoUrl: '',
      removePhoto: true
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const dataToSend = {
      ...formData,
      removePhoto: hasExistingPhoto && !formData.profilePhoto && !formData.photoUrl
    };
    
    onSave(dataToSend);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {initialData ? 'Edit Member' : 'Add New Member'}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              {initialData ? 'Update member information' : 'Register new library member'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FaTimes className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Photo & Member ID Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaIdCard className="inline-block h-4 w-4 mr-2 text-primary-600" />
                  Member ID
                </label>
                <input
                  type="text"
                  name="memberId"
                  value={formData.memberId}
                  onChange={handleChange}
                  required
                  placeholder="M001"
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent uppercase"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaUser className="inline-block h-4 w-4 mr-2 text-primary-600" />
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  placeholder="Member full name"
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Photo Box */}
            <div className="flex flex-col items-center md:items-end space-y-2">
              <div className="relative">
                <div className="w-28 h-28 rounded-lg overflow-hidden border-2 border-gray-200 bg-gradient-to-br from-primary-50 to-gray-50 flex items-center justify-center">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <FaUser className="h-14 w-14 text-gray-400" />
                  )}
                </div>
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white text-xs px-2 py-0.5 rounded">
                  Photo
                </div>
              </div>
              
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <div className="flex items-center gap-1 px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    <FaUpload className="h-3 w-3" />
                    Upload
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                
                {initialData?.photoUrl && !formData.profilePhoto && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="flex items-center gap-1 px-3 py-1 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FaTrash className="h-3 w-3" />
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* DOB & Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaBirthdayCake className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Date of Birth
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaVenusMars className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Gender
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none"
              >
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
          </div>

          {/* Registration Date & Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCalendarAlt className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Registration Date
              </label>
              <input
                type="date"
                name="registrationDate"
                value={formData.registrationDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaPhone className="inline-block h-4 w-4 mr-2 text-primary-600" />
                Contact Number
              </label>
              <input
                type="tel"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                placeholder="Phone number"
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaMapMarkerAlt className="inline-block h-4 w-4 mr-2 text-primary-600" />
              Address
            </label>
            <input
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Complete address"
              className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Quick Tips */}
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
            {/* <p className="font-medium mb-1">💡 Quick Tips:</p> */}
            <ul className="space-y-1">
              <li>• Member ID cannot be changed after creation</li>
              <li>• Supported image formats: JPG, PNG, GIF, Max file size: 5MB</li>
             
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              <FaSave className="h-4 w-4" />
              {initialData ? 'Update Member' : 'Save Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberModal;
